import { useState, type ComponentType } from 'react';
import {
  CheckCircle,
  Clock,
  XCircle,
  RotateCcw,
  // Aliased at the import so the vendor symbol appears once and every use site reads
  // as the affordance it actually is.
  // term-register-skip: lucide-react icon identifier, not the banned verb
  Unlock as ReleaseIcon,
  AlertCircle,
} from 'lucide-react';
import {
  Card,
  Button,
  Heading,
  BodyText,
} from '@aaimplatform/ui';
import {
  useAcknowledgePledge,
  useAcceptPledge,
  useDeclinePledge,
  useReturnPledge,
  useReleasePledge,
  useDenyPledgeReleaseRequest,
} from '../../hooks/usePledges';
import type { PledgeAdmissibleAction } from '../../hooks/usePledges';
import { StepUpAuthModal } from '../../components/staff/StepUpAuthModal';
import { PLATFORM_ADMIN_ROLES } from '../../lib/platform-admin-roles';

/**
 * Who POST /pledges/{pledgeId}/release admits, per the 2026-09-27
 * pledge-release ruling (platform-admin-api pledge.routes.ts,
 * requireReleaseAuthority): an Aaim platform admin on role, or a session holding
 * `pledge:release`, which is seeded to FI_ADMIN and carried by an impersonation
 * session. The server also requires the pledge to be in the caller's own tenant
 * and answers 404 otherwise; this panel renders only on a pledge the session has
 * already read. Roles and permissions compare lowercase on both sides
 * (.claude/rules/authz-case-insensitive.md).
 */
const PLEDGE_RELEASE_ROLES: readonly string[] = PLATFORM_ADMIN_ROLES;
const PLEDGE_RELEASE_PERMISSION = 'pledge:release';

/** True when the session holds a role or the permission the release route admits. */
export function canReleasePledge(
  roles: ReadonlyArray<unknown> | null | undefined,
  permissions: ReadonlyArray<unknown> | null | undefined,
): boolean {
  const lower = (value: unknown): string | null =>
    typeof value === 'string' ? value.toLowerCase() : null;
  const hasRole = (roles ?? []).some((role) => {
    const normalized = lower(role);
    return normalized !== null && PLEDGE_RELEASE_ROLES.includes(normalized);
  });
  return (
    hasRole ||
    (permissions ?? []).some((permission) => lower(permission) === PLEDGE_RELEASE_PERMISSION)
  );
}

/**
 * What a failed release says. A refusal is worded for the reader instead of
 * printing the server's internal message (fi-g1-1). The status rides on the
 * Error the pledge hooks throw (hooks/usePledges.ts unwrap). A 403 means the
 * session holds neither a platform-admin role nor `pledge:release`; since the
 * 2026-09-27 ruling FI staff can hold the permission, so the refusal no longer
 * names Aaim operations as the only releaser.
 */
export function releaseErrorMessage(error: Error | null | undefined): string {
  const status = (error as { status?: unknown } | null | undefined)?.status;
  if (status === 403) return 'Your access does not include releasing pledges.';
  return error?.message ?? 'Failed to release pledge.';
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

function ActionError({ message }: { message: string }) {
  return (
    <div className="bg-error/5 border border-error/20 rounded-sm p-3 flex items-center gap-2 mb-4">
      <AlertCircle className="w-4 h-4 text-error flex-shrink-0" />
      <span className="text-sm font-sans text-error">{message}</span>
    </div>
  );
}

function ActionSuccess({ title, description }: { title: string; description: string }) {
  return (
    <Card>
      <div className="flex items-center gap-3">
        <CheckCircle className="w-5 h-5 text-growth flex-shrink-0" />
        <div>
          <Heading level={3} size="card" className="text-growth">
            {title}
          </Heading>
          <BodyText variant="ui" className="text-secondary text-sm mt-1">
            {description}
          </BodyText>
        </div>
      </div>
    </Card>
  );
}

function NotesInput({
  value,
  onChange,
  label,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  label: string;
  placeholder: string;
}) {
  return (
    <div className="mb-4">
      <label className="block text-label-xs font-sans font-semibold uppercase text-tertiary leading-normal mb-1.5">
        {label}
      </label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={3}
        className="w-full px-3 py-2.5 border border-recessed rounded-sm text-sm font-sans text-primary placeholder:text-tertiary focus:outline-none focus:border-accent resize-none"
      />
    </div>
  );
}

function TextInput({
  value,
  onChange,
  label,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  label: string;
  placeholder: string;
}) {
  return (
    <div className="mb-4">
      <label className="block text-label-xs font-sans font-semibold uppercase text-tertiary leading-normal mb-1.5">
        {label}
      </label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2.5 border border-recessed rounded-sm text-sm font-sans text-primary placeholder:text-tertiary focus:outline-none focus:border-accent"
      />
    </div>
  );
}

function ReasonInput({
  value,
  onChange,
  label,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  label: string;
  placeholder: string;
}) {
  return (
    <div className="mb-4">
      <label className="block text-label-xs font-sans font-semibold uppercase text-tertiary leading-normal mb-1.5">
        {label} <span className="text-error">*</span>
      </label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={3}
        className="w-full px-3 py-2.5 border border-recessed rounded-sm text-sm font-sans text-primary placeholder:text-tertiary focus:outline-none focus:border-accent resize-none"
        required
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// AcknowledgePanel
// ---------------------------------------------------------------------------

export function AcknowledgePanel({ pledgeId }: { pledgeId: string }) {
  const [notes, setNotes] = useState('');
  const acknowledge = useAcknowledgePledge();

  const handleAcknowledge = async () => {
    await acknowledge.mutateAsync({ pledgeId, notes: notes.trim() || undefined });
  };

  if (acknowledge.isSuccess) {
    return (
      <ActionSuccess
        title="Pledge Acknowledged"
        description="This pledge has been marked as acknowledged. The customer will be notified."
      />
    );
  }

  return (
    <Card>
      <div className="flex items-center gap-2 mb-4">
        <Clock className="w-4 h-4 text-warning" />
        <Heading level={3} size="card">
          Acknowledge Pledge
        </Heading>
      </div>
      <BodyText variant="ui" className="text-secondary mb-4">
        Acknowledging confirms your institution has received and is reviewing this
        collateral proposal. This action is recorded in the audit trail.
      </BodyText>

      {acknowledge.isError && (
        <ActionError message={acknowledge.error?.message ?? 'Failed to acknowledge pledge.'} />
      )}

      <NotesInput
        value={notes}
        onChange={setNotes}
        label="Notes (optional)"
        placeholder="Internal notes for this acknowledgment..."
      />

      <Button
        variant="primary"
        icon={CheckCircle}
        onClick={handleAcknowledge}
        disabled={acknowledge.isPending}
      >
        {acknowledge.isPending ? 'Acknowledging...' : 'Acknowledge Pledge'}
      </Button>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// AcceptPanel
// ---------------------------------------------------------------------------

export function AcceptPanel({ pledgeId }: { pledgeId: string }) {
  const [loanReferenceId, setLoanReferenceId] = useState('');
  const [notes, setNotes] = useState('');
  const [stepUpOpen, setStepUpOpen] = useState(false);
  const accept = useAcceptPledge();

  // Accepting is a consequential operation (commits the FI to the collateral
  // package): the button opens the step-up modal; the mutation fires only
  // with a freshly minted operation-bound X-Step-Up-Token. Mirrors the
  // StaffDetail suspend/offboard ceremony.
  const handleStepUpVerified = async (stepUpToken: string) => {
    setStepUpOpen(false);
    await accept.mutateAsync({
      pledgeId,
      body: {
        acceptedBy: '',
        loanReferenceId: loanReferenceId.trim() || undefined,
        notes: notes.trim() || undefined,
      },
      stepUpToken,
    });
  };

  if (accept.isSuccess) {
    return (
      <ActionSuccess
        title="Pledge Accepted"
        description="This collateral package is now ACTIVE. Custodian contract issuance has been triggered. Use the Aaim reference number in your loan system."
      />
    );
  }

  return (
    <Card>
      <div className="flex items-center gap-2 mb-4">
        <CheckCircle className="w-4 h-4 text-growth" />
        <Heading level={3} size="card">
          Accept Pledge
        </Heading>
      </div>
      <BodyText variant="ui" className="text-secondary mb-4">
        Accepting activates this collateral package. Aaim will issue pledge contracts
        to the custodian(s). The pledge ID becomes your permanent reference number.
      </BodyText>

      {accept.isError && (
        <ActionError message={accept.error?.message ?? 'Failed to accept pledge.'} />
      )}

      <TextInput
        value={loanReferenceId}
        onChange={setLoanReferenceId}
        label="Loan Reference ID (optional)"
        placeholder="Your loan system reference number..."
      />

      <NotesInput
        value={notes}
        onChange={setNotes}
        label="Notes (optional)"
        placeholder="Internal notes for this acceptance..."
      />

      <Button
        variant="primary"
        icon={CheckCircle}
        onClick={() => setStepUpOpen(true)}
        disabled={accept.isPending}
      >
        {accept.isPending ? 'Accepting...' : 'Accept and Activate'}
      </Button>

      <StepUpAuthModal
        open={stepUpOpen}
        onOpenChange={setStepUpOpen}
        operation="acceptPledge"
        subjectUserId={pledgeId}
        operationLabel="Accept and activate this pledge"
        operationHash={`acceptPledge:${pledgeId}`}
        onVerified={handleStepUpVerified}
        onCancel={() => setStepUpOpen(false)}
      />
    </Card>
  );
}

// ---------------------------------------------------------------------------
// DeclinePanel
// ---------------------------------------------------------------------------

export function DeclinePanel({ pledgeId }: { pledgeId: string }) {
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [stepUpOpen, setStepUpOpen] = useState(false);
  const decline = useDeclinePledge();

  // Declining is terminal for the member's proposal: step-up gated, same
  // ceremony as accept.
  const handleStepUpVerified = async (stepUpToken: string) => {
    setStepUpOpen(false);
    if (!reason.trim()) return;
    await decline.mutateAsync({
      pledgeId,
      body: {
        declinedBy: '',
        reason: reason.trim(),
        notes: notes.trim() || undefined,
      },
      stepUpToken,
    });
  };

  if (decline.isSuccess) {
    return (
      <ActionSuccess
        title="Pledge Declined"
        description="This pledge has been declined. The customer will see your reason and may submit a new pledge."
      />
    );
  }

  return (
    <Card>
      <div className="flex items-center gap-2 mb-4">
        <XCircle className="w-4 h-4 text-error" />
        <Heading level={3} size="card">
          Decline Pledge
        </Heading>
      </div>
      <BodyText variant="ui" className="text-secondary mb-4">
        Declining rejects this collateral proposal. The customer will see your reason.
        This is a terminal action -- the customer must submit a new pledge.
      </BodyText>

      {decline.isError && (
        <ActionError message={decline.error?.message ?? 'Failed to decline pledge.'} />
      )}

      <ReasonInput
        value={reason}
        onChange={setReason}
        label="Reason (required)"
        placeholder="Reason for declining this pledge (visible to the customer)..."
      />

      <NotesInput
        value={notes}
        onChange={setNotes}
        label="Internal Notes (optional)"
        placeholder="Internal-only notes..."
      />

      <Button
        variant="primary"
        icon={XCircle}
        onClick={() => setStepUpOpen(true)}
        disabled={decline.isPending || !reason.trim()}
        className="bg-error hover:bg-error/90"
      >
        {decline.isPending ? 'Declining...' : 'Decline Pledge'}
      </Button>

      <StepUpAuthModal
        open={stepUpOpen}
        onOpenChange={setStepUpOpen}
        operation="declinePledge"
        subjectUserId={pledgeId}
        operationLabel="Decline this pledge"
        operationHash={`declinePledge:${pledgeId}`}
        onVerified={handleStepUpVerified}
        onCancel={() => setStepUpOpen(false)}
      />
    </Card>
  );
}

// ---------------------------------------------------------------------------
// ReturnPanel
// ---------------------------------------------------------------------------

export function ReturnPanel({ pledgeId }: { pledgeId: string }) {
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const returnPledge = useReturnPledge();

  const handleReturn = async () => {
    if (!reason.trim()) return;
    await returnPledge.mutateAsync({
      pledgeId,
      body: {
        returnedBy: '',
        reason: reason.trim(),
        notes: notes.trim() || undefined,
      },
    });
  };

  if (returnPledge.isSuccess) {
    return (
      <ActionSuccess
        title="Pledge Returned"
        description="This pledge has been returned for revision. The customer will see your instructions and can modify and resubmit."
      />
    );
  }

  return (
    <Card>
      <div className="flex items-center gap-2 mb-4">
        <RotateCcw className="w-4 h-4 text-warning" />
        <Heading level={3} size="card">
          Return for Revision
        </Heading>
      </div>
      <BodyText variant="ui" className="text-secondary mb-4">
        Returning sends this pledge back to the customer with instructions on what
        needs to change. They can modify and resubmit.
      </BodyText>

      {returnPledge.isError && (
        <ActionError message={returnPledge.error?.message ?? 'Failed to return pledge.'} />
      )}

      <ReasonInput
        value={reason}
        onChange={setReason}
        label="Revision Instructions (required)"
        placeholder="What the customer needs to change (visible to the customer)..."
      />

      <NotesInput
        value={notes}
        onChange={setNotes}
        label="Internal Notes (optional)"
        placeholder="Internal-only notes..."
      />

      <Button
        variant="secondary"
        icon={RotateCcw}
        onClick={handleReturn}
        disabled={returnPledge.isPending || !reason.trim()}
      >
        {returnPledge.isPending ? 'Returning...' : 'Return for Revision'}
      </Button>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// ReleasePanel
// ---------------------------------------------------------------------------

export function ReleasePanel({ pledgeId }: { pledgeId: string }) {
  const [loanReferenceId, setLoanReferenceId] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const release = useReleasePledge();

  // No actor field: the server records the releasing actor from the session
  // (ruling release-request-released-by, 2026-09-28).
  const handleRelease = async () => {
    await release.mutateAsync({
      pledgeId,
      body: {
        loanReferenceId: loanReferenceId.trim() || undefined,
        reason: reason.trim() || undefined,
        notes: notes.trim() || undefined,
      },
    });
  };

  // Until a custodian acknowledges the release, the copy says who is
  // notified and claims no lifted hold: nothing yet asks the custodian to lift
  // it (GH #2957 deliverable 3, ruling unit fi-15). Future tense: when the
  // POST returns, the notices are queued on the outbox, not yet delivered.
  if (release.isSuccess) {
    return (
      <ActionSuccess
        title="Pledge Released"
        description="Your institution and the customer will be notified."
      />
    );
  }

  return (
    <Card>
      <div className="flex items-center gap-2 mb-4">
        {/* Decoration, not the CTA: the Release Pledge button keeps the
            red (fi-g1-9). */}
        <ReleaseIcon className="w-4 h-4 text-tertiary" aria-hidden="true" />
        <Heading level={3} size="card">
          Release Pledge
        </Heading>
      </div>
      <BodyText variant="ui" className="text-secondary mb-4">
        A pledge is typically released when the associated loan is paid off. Your
        institution and the customer are notified.
      </BodyText>

      {release.isError && <ActionError message={releaseErrorMessage(release.error)} />}

      <TextInput
        value={loanReferenceId}
        onChange={setLoanReferenceId}
        label="Loan Reference ID (optional)"
        placeholder="Loan system reference for cross-referencing..."
      />

      <NotesInput
        value={reason}
        onChange={setReason}
        label="Reason (optional)"
        placeholder="Reason for release..."
      />

      <NotesInput
        value={notes}
        onChange={setNotes}
        label="Notes (optional)"
        placeholder="Internal notes..."
      />

      <Button
        variant="primary"
        icon={ReleaseIcon}
        onClick={handleRelease}
        disabled={release.isPending}
      >
        {release.isPending ? 'Releasing...' : 'Release Pledge'}
      </Button>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// DenyReleaseRequestPanel
// ---------------------------------------------------------------------------

/**
 * GH #2280: the customer asked for this pledge's release. Approving is the
 * Release panel; this one denies the request and leaves the pledge active.
 * No step-up, as release takes none (operator ruling 2026-10-23). The server
 * records the denying actor from the session, as it does for decline and
 * return, so `deniedBy` goes out empty like theirs.
 */
export function DenyReleaseRequestPanel({ pledgeId }: { pledgeId: string }) {
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const deny = useDenyPledgeReleaseRequest();

  const handleDeny = async () => {
    if (!reason.trim()) return;
    await deny.mutateAsync({
      pledgeId,
      body: {
        deniedBy: '',
        reason: reason.trim(),
        notes: notes.trim() || undefined,
      },
    });
  };

  if (deny.isSuccess) {
    return (
      <ActionSuccess
        title="Release Request Denied"
        description="The pledge stays active. The customer will see your reason."
      />
    );
  }

  return (
    <Card>
      <div className="flex items-center gap-2 mb-4">
        <XCircle className="w-4 h-4 text-tertiary" aria-hidden="true" />
        <Heading level={3} size="card">
          Deny Release Request
        </Heading>
      </div>
      <BodyText variant="ui" className="text-secondary mb-4">
        The customer asked your institution to release this pledge. To approve,
        release it above. Denying keeps the pledge active, and the customer
        will see your reason.
      </BodyText>

      {deny.isError && (
        <ActionError message={deny.error?.message ?? 'Failed to deny the release request.'} />
      )}

      <ReasonInput
        value={reason}
        onChange={setReason}
        label="Reason (required)"
        placeholder="Why the pledge stays in place (visible to the customer)..."
      />

      <NotesInput
        value={notes}
        onChange={setNotes}
        label="Internal Notes (optional)"
        placeholder="Internal-only notes..."
      />

      <Button
        variant="secondary"
        icon={XCircle}
        onClick={handleDeny}
        disabled={deny.isPending || !reason.trim()}
      >
        {deny.isPending ? 'Denying...' : 'Deny Release Request'}
      </Button>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// PledgeActions -- renders the panels the server lists as admissible
// ---------------------------------------------------------------------------

/**
 * One panel per admissible action. Exhaustive over the generated
 * PledgeAdmissibleAction union: a new spec member without a panel is a tsc
 * error. Key order is render order.
 */
const ACTION_PANELS: Record<PledgeAdmissibleAction, ComponentType<{ pledgeId: string }>> = {
  PLEDGE_ACKNOWLEDGED: AcknowledgePanel,
  PLEDGE_ACCEPTED: AcceptPanel,
  PLEDGE_DECLINED: DeclinePanel,
  PLEDGE_RETURNED: ReturnPanel,
  PLEDGE_RELEASED: ReleasePanel,
  // GH #2280. Listed by the server only while the pledge has an OPEN release
  // request; it renders after Release, the approving act.
  PLEDGE_RELEASE_REQUEST_DENIED: DenyReleaseRequestPanel,
};

const PANEL_ORDER = Object.keys(ACTION_PANELS) as PledgeAdmissibleAction[];

interface PledgeActionsProps {
  pledgeId: string;
  /**
   * PledgeDetailResponse.admissibleActions (GH #2599). The server derives it
   * from the pledge's pinned workflow recipe, its status, its acknowledgment
   * and the caller's permissions, with the same gates the action routes
   * enforce. Absent means the server could not resolve the recipe, and no
   * action renders.
   */
  admissibleActions?: readonly PledgeAdmissibleAction[];
}

export function PledgeActions({ pledgeId, admissibleActions }: PledgeActionsProps) {
  if (!admissibleActions || admissibleActions.length === 0) return null;
  const listed = new Set(admissibleActions);
  return (
    <div className="space-y-4">
      {PANEL_ORDER.filter((action) => listed.has(action)).map((action) => {
        const Panel = ACTION_PANELS[action];
        return <Panel key={action} pledgeId={pledgeId} />;
      })}
    </div>
  );
}
